"use client";

import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "next/navigation";
import Link from "next/link";
import DisputeModal from "@/components/DisputeModal";
import PiBotLoader from "@/components/PiBotLoader";

const LAST_UPDATED = "April 04, 2026";

function useOpenFromQuery() {
  const [open, setOpen] = useState(false);
  const params = useSearchParams();

  useEffect(() => {
    const v = (params?.get("check") || "").toLowerCase();
    if (["1", "true", "yes"].includes(v)) setOpen(true);
  }, [params]);

  return [open, setOpen];
}

function useMascotFromQuery() {
  const params = useSearchParams();
  const clampSize = (n) => Math.max(64, Math.min(220, n || 0));
  const toBool = (v) => ["1", "true", "yes"].includes((v || "").toLowerCase());

  const mood = (params?.get("mood") || "happy").toLowerCase();
  const blink = params?.get("blink");
  const wave = params?.get("wave");
  const size = params?.get("size");
  const accent = params?.get("accent");
  const muted = params?.get("muted");

  return {
    mood: ["happy", "neutral", "wink"].includes(mood) ? mood : "happy",
    blink: blink == null ? true : toBool(blink),
    wave: wave == null ? false : toBool(wave),
    size: size ? clampSize(Number(size)) : 112,
    accent: accent || "#F38B21",
    muted: muted || "#2A2A2A",
  };
}

function useBusinessHoursString() {
  return useMemo(() => {
    try {
      const opts = { hour: "numeric", minute: "2-digit" };
      const localFmt = new Intl.DateTimeFormat(undefined, opts);

      const nyHours = "9:00 AM–3:00 PM";
      const localStart = localFmt.format(new Date("2025-10-01T13:00:00Z"));
      const localEnd = localFmt.format(new Date("2025-10-01T19:00:00Z"));

      return `Mon–Fri, ${nyHours} (New York). In your local time: ${localStart}–${localEnd}.`;
    } catch {
      return "Mon–Fri, 9:00 AM–3:00 PM (New York).";
    }
  }, []);
}

export default function TrustSecurity() {
  const [open, setOpen] = useOpenFromQuery();
  const businessHours = useBusinessHoursString();
  const mascotFromUrl = useMascotFromQuery();

 

  const mascot = { ...mascotFromUrl, wave: open || mascotFromUrl.wave };

  return (
    <div className="max-w-3xl mx-auto p-6 text-textPrimary">
    
      <nav className="text-xs text-textSecondary mb-3">
        <a href="#agent-payments" className="underline mr-3">AI agent checkout</a>
        <a href="#content-rights" className="underline mr-3">Content & rights</a>
        <a href="#status-checks" className="underline mr-3">Status checks</a>
        <a href="#security" className="underline mr-3">Security</a>
        <a href="#payment-modes" className="underline mr-3">Payment methods</a>
        <a href="#refunds" className="underline mr-3">Refunds & Resolution</a>
        <a href="#failures" className="underline">If something fails</a>
      </nav>

      <div className="flex items-start justify-between gap-4 mb-2">
        <h1 className="text-2xl font-semibold">Trust &nbsp;Security</h1>
        <PiBotLoader
          {...mascot}
          guardian
          ritual
          wave
          showScroll
          scrollText="SAFU"
          size={Math.min(mascot.size, 88)}
          className="shrink-0 block sm:ml-2"
          decorative
          style={{ filter: "drop-shadow(0 2px 6px rgba(0,0,0,.15))" }}
          label="Our Mascot"
        />
      </div>

      <p className="text-sm text-primary font-medium mb-2">Why ReloadPi is safe to use.</p>

      <div className="mb-4 text-xs text-textSecondary">
        Last updated: {LAST_UPDATED} • Version 1.0
      </div>

      <section
        id="agent-payments"
        className="mb-6 rounded-2xl border border-primary/30 bg-primary/5 p-5"
      >
        <div className="flex flex-wrap items-center gap-2 mb-2">
          <span className="inline-flex items-center gap-1.5 rounded-full border border-primary/40 bg-primary/10 px-2.5 py-0.5 text-[10px] font-medium uppercase tracking-widest text-primary">
            <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
            New
          </span>
          <h2 className="text-lg font-semibold">Let an AI agent check out for you</h2>
        </div>

        <p className="text-sm text-textPrimary mb-3">
          Connect ReloadPi to Claude, Cursor, or any MCP-compatible AI and let it browse and
          buy eSIMs, top-ups, and gift vouchers for you. The agent gets exactly the data it
          needs to complete a purchase — and nothing more. This removes whole classes of risk
          that come with sending crypto wallet-to-wallet by hand.
        </p>

        <ul className="grid gap-2 sm:grid-cols-2 text-sm text-textPrimary mb-4">
          <li className="rounded-xl border border-border bg-background/40 p-3">
            <b>No address copy-paste.</b> The agent pays via the x402 protocol — you never
            paste a wallet address into a chat or approve a manual transfer to a stranger.
          </li>
          <li className="rounded-xl border border-border bg-background/40 p-3">
            <b>Nothing to phish.</b> No API key, no account, no KYC — the payment itself is
            the authentication, so there are no credentials to steal.
          </li>
          <li className="rounded-xl border border-border bg-background/40 p-3">
            <b>Your seed stays yours.</b> The flow never asks for — or touches — your wallet
            seed phrase or passphrase. ReloadPi never will.
          </li>
          <li className="rounded-xl border border-border bg-background/40 p-3">
            <b>Minimal data.</b> The agent passes only the fields a purchase requires (such
            as a recipient number or email) — the same minimal-exposure rule we apply everywhere.
          </li>
          <li className="rounded-xl border border-border bg-background/40 p-3">
            <b>Deterministic refunds.</b> Failed eSIM and carrier-rejected top-up orders
            refund automatically in USDC within ~5 minutes;{" "}
            <code className="font-mono text-xs">claim_refund</code> is the manual fallback.
          </li>
          <li className="rounded-xl border border-border bg-background/40 p-3">
            <b>On-chain auditable.</b> Every order carries a payment txHash, so a dropped
            response is never a lost order — it can be recovered straight from the hash.
          </li>
        </ul>

        <div className="flex flex-wrap items-center gap-3">
          <Link
            href="/connect"
            className="rounded-xl border border-primary/40 bg-primary/10 px-4 py-2 text-sm font-medium text-primary hover:bg-primary/20"
          >
            Connect your AI →
          </Link>
          <span className="text-xs text-textSecondary">
            Works with Claude &amp; Cursor · pays in USDC on Base · no account needed
          </span>
        </div>
      </section>

      <p className="text-textPrimary mb-6">
        Every ReloadPi transaction follows a clear, auditable flow. Payments are verified,
        orders are tracked, and transaction status can be checked using the TXID. We only
        use the minimum information required to complete and support your order securely.
      </p>

      <Section id="content-rights" title="Content, copyright & takedowns (eBooks)">
        <div className="text-sm text-textPrimary space-y-2">
          <p>
            ReloadPi may allow creators to upload and sell digital content such as eBooks.
            Creators are responsible for ensuring they own, license, or otherwise have permission
            to distribute any content they upload, including cover images.
          </p>

          <p>
            If you believe content on ReloadPi infringes your copyright or other intellectual
            property rights, contact us at{" "}
            <a
              href="mailto:support@reloadpi.com?subject=IP%20Infringement%20Report"
              className="underline hover:opacity-80"
            >
              support@reloadpi.com
            </a>{" "}
            with: (1) the link or title of the content, (2) proof of ownership or authorization,
            and (3) your contact information. We may remove or restrict access to the content
            while we review the report.
          </p>

          <p className="text-xs text-textSecondary">
            For full details, see our{" "}
            <Link href="/terms" className="underline hover:opacity-80">
              Terms of Service
            </Link>
            .
          </p>
        </div>
      </Section>

      <Section id="status-checks" title="How status checks work">
        <ul className="list-disc pl-5 space-y-2 text-sm text-textPrimary">
          <li>Enter your <b>transaction ID (TXID)</b> to check the status of your order.</li>
          <li>Possible results: <b>PENDING / AUTHORIZED / IN_PROGRESS / DONE / FAILED</b>.</li>
          <li>Status checks never display voucher codes, phone numbers, or other personal information.</li>
        </ul>

        <div className="mt-4">
          <button
            onClick={() => setOpen(true)}
            className="rounded-xl border border-border px-4 py-2 hover:bg-background"
            type="button"
          >
            Check a Transaction
          </button>
        </div>
      </Section>

      <Section id="security" title="Security practices">
        <ul className="list-disc pl-5 space-y-2 text-sm text-textPrimary">
          <li><b>Strict input validation</b> helps prevent abuse and invalid lookups.</li>
          <li><b>Rate-limited APIs</b> protect the service from spam and misuse.</li>
          <li><b>HTTPS-only connections</b> are used across ReloadPi.</li>
          <li><b>Minimal data exposure</b>: status and dispute tools do not return voucher codes, phone numbers, or unnecessary personal data.</li>
          <li><b>No passphrase ever required</b>: ReloadPi will never ask for your Pi wallet passphrase.</li>
        </ul>
      </Section>

      <Section id="payment-modes" title="Payment methods">
        <p className="text-sm text-textPrimary mb-3">
          ReloadPi supports multiple payment options, and each one may have different wallet,
          network, or browser requirements. Before paying, please review the{" "}
          <Link href="/payments" className="underline hover:opacity-80">
            Payment Options
          </Link>{" "}
          page to choose the method that matches your setup.
        </p>

        <ul className="list-disc pl-5 space-y-2 text-sm text-textPrimary">
          <li>
            <b>Pi payments</b> must be completed through the official Pi Browser flow when available.
          </li>
          <li>
            <b>Other crypto payments</b> must be sent on the correct network and according to the instructions shown during checkout.
          </li>
          <li>
            Using the wrong app, browser, wallet, or network can cause payment errors, failed checkout, or a transaction remaining <b>PENDING</b> until resolved.
          </li>
          <li>
            Always verify the selected payment method and network before confirming any transaction.
          </li>
        </ul>

        <p className="text-xs text-textSecondary mt-3">
          For supported coins, wallets, and network-specific guidance, visit the{" "}
          <Link href="/payments" className="underline hover:opacity-80">
            Payment Options
          </Link>{" "}
          page.
        </p>
      </Section>

      <Section id="refunds" title="Refunds & Resolution">
        <p className="text-sm text-textPrimary mb-3">
          Digital goods are generally <b>non-refundable after successful delivery</b>. If delivery fails
          or a supported issue occurs, ReloadPi applies the resolution rules below.
        </p>

        <RefundMatrix />

        <div className="mt-4 space-y-2 text-sm text-textPrimary">
          <PolicyItem
            title="Top-ups — wrong number but processed (DONE)"
            body="If delivery is confirmed to the number you entered, the transaction cannot be reversed."
          />
          <PolicyItem
            title="Top-ups — failed due to invalid or unreachable number (FAILED)"
            body="We refund 75% because carriers may charge non-delivery fees."
          />
          <PolicyItem
            title="Long queues & early cancel (PENDING / IN_PROGRESS)"
            body={`Some services may remain in queue for several hours. If you cancel early, you receive a 100% refund. If the order is already locked for fulfillment, it must finish processing. If it later fails, the applicable refund is issued automatically. Orders with no delivery after 24 hours may be auto-canceled.\n⚠️ Manual cancel requests are processed during: ${businessHours}`}
          />
          <PolicyItem
            title="Duplicate purchases"
            body="If two identical purchases occur within 30 minutes and only one is delivered, the duplicate is refunded."
          />
          <PolicyItem
            title="Unauthorized payments"
            body={`Report suspected unauthorized payments within 48 hours and include your TXID, Pi username, memo, and date/time. We review these cases promptly and may temporarily restrict related receiving accounts during investigation.\nReloadPi will never ask for your passphrase.`}
          />
          <PolicyItem
            title="How refunds are paid"
            body="Refunds are returned in Pi through the original payment channel."
          />
        </div>

        <div className="mt-4">
          <button
            onClick={() => setOpen(true)}
            className="rounded-xl border border-border px-4 py-2 hover:bg-background"
            type="button"
          >
            Open Status / Dispute
          </button>
        </div>
      </Section>

      <Section id="failures" title="If something fails">
        <div className="text-sm text-textPrimary space-y-2">
          <p>
            If your transaction shows <b>FAILED</b>, contact ReloadPi with your <b>TXID</b> through one of
            the support channels below:
          </p>
          <ul className="list-disc pl-5 space-y-1 marker:text-textSecondary">
            <li>
              Create a thread in the{" "}
              <Link href="/community/category/support" className="underline hover:opacity-80">
                Support Forum
              </Link>
              .
            </li>
            <li>DM a moderator on Telegram.</li>
            <li>
              Email:{" "}
              <a
                href="mailto:support@reloadpi.com?subject=FAILED%20TXID%20Dispute"
                className="underline hover:opacity-80"
              >
                support@reloadpi.com
              </a>
            </li>
          </ul>
          <p className="text-xs">Tip: share only your TXID and the expected result.</p>
        </div>
      </Section>

      <DisputeModal id="dispute-modal" open={open} onClose={() => setOpen(false)} />
    </div>
  );
}

function Section({ id, title, children }) {
  return (
    <section id={id} className="mb-6">
      <h2 className="text-lg font-semibold mb-2">{title}</h2>
      {children}
    </section>
  );
}

function PolicyItem({ title, body }) {
  return (
    <div className="rounded-xl border border-border p-3">
      <div className="font-medium">{title}</div>
      <div className="text-textPrimary whitespace-pre-line">{body}</div>
    </div>
  );
}

function RefundMatrix() {
  return (
    <div className="overflow-hidden rounded-xl border border-border">
      <table className="w-full text-sm">
        <thead className="bg-background/50 border-b border-border text-xs font-medium">
          <tr>
            <th scope="col" className="text-left p-2">Status</th>
            <th scope="col" className="text-left p-2">Example</th>
            <th scope="col" className="text-left p-2">Refund</th>
          </tr>
        </thead>
        <tbody>
          <Row
            status="PENDING / AUTHORIZED"
            example="Payment created or confirmed, awaiting fulfillment"
            refund="Cancelable — 100% if canceled before delivery"
          />
          <Row
            status="IN_PROGRESS"
            example="Provider is processing the order"
            refund="If over 24h without delivery → 100% refund"
          />
          <Row
            status="DONE"
            example="Digital code or top-up delivered successfully"
            refund="Non-refundable if delivered to the details you provided"
          />
          <Row
            status="FAILED"
            example="Provider or carrier could not complete delivery"
            refund="Digital goods: 100% • Top-ups invalid/unreachable: 75%"
          />
        </tbody>
      </table>
    </div>
  );
}

function Row({ status, example, refund }) {
  return (
    <tr className="border-b border-border last:border-b-0">
      <th scope="row" className="p-2 font-medium align-top">{status}</th>
      <td className="p-2 text-textPrimary align-top">{example}</td>
      <td className="p-2 align-top">{refund}</td>
    </tr>
  );
}